import React, { useEffect, useState } from "react";
import withLayout from "../../hocs/Layout";
import { Button, Divider, Grid, Typography, Box } from "@mui/material";
import { FormProvider, RHFDateRangePickerField, RHFTextField } from "../../common/hook-form";
import { useForm } from "react-hook-form";
import { DoneAllOutlined, PauseCircle, PublishedWithChangesOutlined, UploadFileOutlined, WhatsApp, PhoneInTalk, CheckCircleOutline, CancelOutlined } from "@mui/icons-material";
import ProzoDataGrid from "../../common/ProzoDataGrid";
import MUIModal from "../../common/MUIModal";
import { buildFilterConditions, parseDateAndTime } from "../../helpers/UtilityHelper";
import { useGetNPRDataQuery } from "../../redux/commonRTK";
import PendingNprData from "./PendingNpr";
import InitiatedNdrData from "./InitiatedNpr";
import ClosedNPrData from "./ClosedNpr";
import NPRBulkUpload from "./BulkUploads";
import { getMerchant } from "../../helpers/ReduxHelpers";
import { getGeneric } from "../../common/constants";
import PageTitle from "../../common/PageTitle";
import { FONT, SIZE, WEIGHT } from '../../styles/foundation/typography';
import { NAVY, BORDER, SUBTLE, WHITE, PRIMARY_BLUE, SUCCESS, ERROR, WARNING, withOpacity } from '../../styles/foundation/colors';
import { pageContainerSx, filterBarSx, primaryBtnSx, secondaryBtnSx } from '../../styles/patterns';
import { primaryTextSx, secondaryTextSx, cellStackSx } from '../orders/list2/cellStyles';

const NPRListing = () => {

    const [filterChange, setFiltersChange] = useState(0);
    const [filters, setFilters] = useState(0);
    const [active, setActive] = useState(0);
    const [reload, setReload] = useState(0);
    const filterConditions = buildFilterConditions(filters);

    const merchant = getMerchant();
    const merchantId = merchant.id ? merchant.id : ''
    const [interventionConfig, setInterventionConfig] = useState(null);

    // NPR calling / WhatsApp activation flags. Same endpoint as the NDR page, with type=NPR —
    // the backend returns NPR values under the NDR-named fields (whatsappAutomatedNdr =
    // NPR WhatsApp-initiate; futworksNdrConfig/futworkAINdrConfig = NPR Futwork / Futwork-AI calling).
    useEffect(() => {
        if (!merchantId) return;
        getGeneric('intervention/rules/config', { merchantOid: merchantId, type: 'NPR' })
            .then((res) => { setInterventionConfig(res?.data) })
            .catch(() => { setInterventionConfig(null) })
    }, [merchantId]);

    const nprWhatsappActive = !!interventionConfig?.whatsappAutomatedNdr;
    const nprCallingActive = !!(interventionConfig?.futworksNdrConfig?.pushLead || interventionConfig?.futworkAINdrConfig?.pushLead);

    const { data: pendingData, refetch } = useGetNPRDataQuery({
        andfilter: [
            {
                "field": "orderStatus",
                "operator": "in",
                "value": [
                    "PICKUP_FAILED"
                ]
            },
            {
                "field": "is_reverse",
                "operator": "eq",
                "value": true
            },
            {
                "field": "nprData.nprStatus",
                "operator": "in",
                "value": [
                    "REPORTED"
                ]
            },
            { field: "merchant.$id", operator: "eq", value: merchantId },
            ...filterConditions.andfilter
        ],
        offset: 0,
        limit: 1,
        includes: "id",
    }, [reload])

    const { data: initiatedData, refetch: refetch2 } = useGetNPRDataQuery({
        andfilter: [
            {
                "field": "is_reverse",
                "operator": "eq",
                "value": true
            },
            {
                "field": "nprData.nprStatus",
                "operator": "in",
                "value": [
                    "INITIATED"
                ]
            },
            { field: "merchant.$id", operator: "eq", value: merchantId },
            ...filterConditions.andfilter
        ],
        offset: 0,
        limit: 1,
        includes: "id",
    }, [reload]);

    useEffect(() => {
        refetch();
        refetch2()
    }, [reload, refetch, refetch2]);

    const methods = useForm();
    const {
        handleSubmit,
        reset,
    } = methods;

    const onSubmit = (data) => {
        setFiltersChange(filterChange + 1)
        setFilters(data);
    }

    const pendingCount = pendingData?.count || 0;
    const initiatedCount = initiatedData?.count || 0;

    const tabs = [
        { label: 'Pending', icon: <PauseCircle />, count: pendingCount, color: ERROR },
        { label: 'Initiated', icon: <PublishedWithChangesOutlined />, count: initiatedCount, color: WARNING },
        { label: 'Closed', icon: <DoneAllOutlined />, count: null, color: SUCCESS },
        { label: 'Bulk Actions', icon: <UploadFileOutlined />, count: null, color: PRIMARY_BLUE },
    ];

    return (
        <Box sx={pageContainerSx}>
            <PageTitle subTitle="Manage pending, initiated and closed NPR shipments">NPR Management</PageTitle>

            {/* NPR calling / WhatsApp messaging activation status (intervention/rules/config?type=NPR) */}
            {interventionConfig && (
                <Box sx={{ display: 'flex', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
                    <Box sx={{
                        display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1,
                        bgcolor: WHITE, border: `1px solid ${BORDER}`, borderRadius: '10px',
                    }}>
                        <Box sx={{
                            width: 28, height: 28, borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                            bgcolor: nprWhatsappActive ? SUCCESS + '14' : ERROR + '14',
                            color: nprWhatsappActive ? SUCCESS : ERROR,
                        }}>
                            <WhatsApp sx={{ fontSize: 16 }} />
                        </Box>
                        <Typography sx={{ fontFamily: FONT, fontSize: SIZE['2xs'], color: NAVY, fontWeight: WEIGHT.medium }}>
                            WhatsApp NPR
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                            {nprWhatsappActive
                                ? <CheckCircleOutline sx={{ fontSize: 14, color: SUCCESS }} />
                                : <CancelOutlined sx={{ fontSize: 14, color: ERROR }} />}
                            <Typography sx={{ fontFamily: FONT, fontSize: SIZE['2xs'], fontWeight: WEIGHT.bold, color: nprWhatsappActive ? SUCCESS : ERROR }}>
                                {nprWhatsappActive ? 'Enabled' : 'Disabled'}
                            </Typography>
                        </Box>
                    </Box>
                    <Box sx={{
                        display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1,
                        bgcolor: WHITE, border: `1px solid ${BORDER}`, borderRadius: '10px',
                    }}>
                        <Box sx={{
                            width: 28, height: 28, borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                            bgcolor: nprCallingActive ? SUCCESS + '14' : ERROR + '14',
                            color: nprCallingActive ? SUCCESS : ERROR,
                        }}>
                            <PhoneInTalk sx={{ fontSize: 16 }} />
                        </Box>
                        <Typography sx={{ fontFamily: FONT, fontSize: SIZE['2xs'], color: NAVY, fontWeight: WEIGHT.medium }}>
                            NPR Calling
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                            {nprCallingActive
                                ? <CheckCircleOutline sx={{ fontSize: 14, color: SUCCESS }} />
                                : <CancelOutlined sx={{ fontSize: 14, color: ERROR }} />}
                            <Typography sx={{ fontFamily: FONT, fontSize: SIZE['2xs'], fontWeight: WEIGHT.bold, color: nprCallingActive ? SUCCESS : ERROR }}>
                                {nprCallingActive ? 'Active' : 'Inactive'}
                            </Typography>
                        </Box>
                    </Box>
                </Box>
            )}

            {/* Status tabs */}
            <Box sx={{ bgcolor: WHITE, border: `1px solid ${BORDER}`, borderRadius: '12px', mb: 2, overflow: 'hidden' }}>
                <Box sx={{ display: 'flex' }}>
                    {tabs.map((t, i) => {
                        const isActive = active === i;
                        const countDisplay = t.count != null ? (t.count > 99999 ? '99,999+' : t.count?.toLocaleString?.() ?? '—') : null;
                        return (
                            <React.Fragment key={t.label}>
                                {i > 0 && <Divider orientation="vertical" flexItem sx={{ borderColor: BORDER }} />}
                                <Box
                                    onClick={() => { setActive(i); setFiltersChange(filterChange + 1); }}
                                    sx={{
                                        flex: '1 1 auto', minWidth: 130, py: 1.25, px: 2, cursor: 'pointer',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1,
                                        borderBottom: isActive ? `3px solid ${t.color}` : '3px solid transparent',
                                        bgcolor: isActive ? withOpacity(t.color, 0.06) : 'transparent',
                                        transition: 'all 0.15s',
                                        '&:hover': { bgcolor: withOpacity(t.color, 0.04) },
                                    }}
                                >
                                    <Box sx={{
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        width: 30, height: 30, borderRadius: '8px',
                                        bgcolor: isActive ? withOpacity(t.color, 0.12) : withOpacity(t.color, 0.07),
                                        color: isActive ? t.color : SUBTLE,
                                        '& .MuiSvgIcon-root': { fontSize: 17 },
                                    }}>
                                        {t.icon}
                                    </Box>
                                    <Box>
                                        <Typography sx={{
                                            fontFamily: FONT, fontSize: SIZE['2xs'], fontWeight: WEIGHT.medium,
                                            color: isActive ? NAVY : SUBTLE, lineHeight: 1.2,
                                        }}>
                                            {t.label}
                                        </Typography>
                                        {countDisplay != null && (
                                            <Typography sx={{
                                                fontFamily: FONT, fontSize: SIZE.sm, fontWeight: WEIGHT.bold,
                                                color: isActive ? t.color : NAVY, lineHeight: 1.3,
                                            }}>
                                                {countDisplay}
                                            </Typography>
                                        )}
                                    </Box>
                                </Box>
                            </React.Fragment>
                        );
                    })}
                </Box>
            </Box>

            {/* Filter bar for Pending/Initiated/Closed tabs */}
            {[0, 1, 2].includes(active) && (
                <Box data-rp-filters sx={filterBarSx}>
                    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
                        <Grid container spacing={2} alignItems="center">
                            <Grid item xs={12} sm={6} md={2}>
                                <RHFTextField name="reference" label="Order Number" size="small" />
                            </Grid>
                            <Grid item xs={12} sm={6} md={2}>
                                <RHFTextField name="awb" label="AWB Number" size="small" />
                            </Grid>
                            <Grid item xs={12} sm={6} md={2}>
                                <RHFTextField name="city" label="City" size="small" />
                            </Grid>
                            <Grid item xs={12} sm={6} md={2}>
                                <RHFTextField name="state" label="State" size="small" />
                            </Grid>
                            <Grid item xs={12} sm={6} md={2}>
                                <RHFDateRangePickerField name="created_date" label="Created Date" maxDate={new Date()} />
                            </Grid>
                            <Grid item xs={12} sm={6} md={2}>
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                    <Button type="submit" variant="contained" size="small" sx={primaryBtnSx}>Filter</Button>
                                    <Button variant="outlined" size="small" sx={secondaryBtnSx} onClick={() => {
                                        reset({ state: '', city: '', awb: '', customerName: '', reference: '', date: '' })
                                        setFiltersChange(filterChange + 1);
                                        setFilters(0);
                                    }}>Reset</Button>
                                </Box>
                            </Grid>
                        </Grid>
                    </FormProvider>
                </Box>
            )}

            {/* Tab content */}
            {active === 0 && <PendingNprData filters={filters} filterChange={filterChange} setReload={setReload} />}
            {active === 1 && <InitiatedNdrData filters={filters} filterChange={filterChange} />}
            {active === 2 && <ClosedNPrData filters={filters} filterChange={filterChange} />}
            {active === 3 && <NPRBulkUpload />}
        </Box>
    );
};

export function ActionLogModal(props) {
    const { data, logModal, setLogModal } = props;
    const actionLogModal = [
        {
            field: "nprAction",
            headerAlign: "left",
            hideSortIcons: true,
            headerName: "Action",
            flex: 0.6,
            minWidth: 100,
        },
        {
            field: "cpResponse",
            headerAlign: "left",
            hideSortIcons: true,
            headerName: "Action Status",
            flex: 0.6,
            minWidth: 120,
        },
        {
            field: "actionTakenBy",
            headerAlign: "left",
            hideSortIcons: true,
            headerName: "Action Taken By",
            flex: 1,
            minWidth: 140,
        },
        {
            field: "nprCurrentAttemptDate",
            headerAlign: "left",
            hideSortIcons: true,
            headerName: "Reattempt Request Date",
            flex: 0.8,
            minWidth: 160,
            renderCell: (params) => {
                const { row } = params;
                return (
                    <Typography sx={secondaryTextSx}>
                        {parseDateAndTime(row?.nprCurrentAttemptDate, true)}
                    </Typography>
                )
            },
        },
        {
            field: "actionDate",
            headerAlign: "left",
            hideSortIcons: true,
            headerName: "Action Date",
            flex: 0.8,
            minWidth: 140,
            renderCell: (params) => {
                const { row } = params;
                return (
                    <Typography sx={secondaryTextSx}>
                        {parseDateAndTime(row?.actionDate)}
                    </Typography>
                )
            },
        },
    ];

    return (
        <MUIModal
            open={logModal}
            title="NPR Action Log"
            handleClose={() => setLogModal(false)}
        >
            <ProzoDataGrid
                autoHeight={true}
                columns={actionLogModal}
                rows={data ? data : []}
                paginationMode="client"
                rowCount={data?.length}
                getRowId={(row) => row.actionDate}
            />
        </MUIModal>
    )
}

export function ActionLogCloseModal(props) {
    const { data, logModal, setLogModal } = props;
    const actionLogModal = [
        {
            field: "nprAction",
            headerAlign: "left",
            hideSortIcons: true,
            headerName: "Action",
            flex: 0.6,
            minWidth: 100,
        },
        {
            field: "cpResponse",
            headerAlign: "left",
            hideSortIcons: true,
            headerName: "CP Response",
            flex: 0.6,
            minWidth: 120,
        },
        {
            field: "actionTakenBy",
            headerAlign: "left",
            hideSortIcons: true,
            headerName: "Action Taken By",
            flex: 1,
            minWidth: 140,
        },
        {
            field: "actionDate",
            headerAlign: "left",
            hideSortIcons: true,
            headerName: "Action Date",
            flex: 0.8,
            minWidth: 140,
            renderCell: (params) => {
                const { row } = params;
                return (
                    <Typography sx={secondaryTextSx}>
                        {parseDateAndTime(row?.actionDate)}
                    </Typography>
                )
            },
        },
    ];

    return (
        <MUIModal
            open={logModal}
            title="NPR Action Log"
            handleClose={() => setLogModal(false)}
        >
            <ProzoDataGrid
                autoHeight={true}
                columns={actionLogModal}
                rows={data ? data : []}
                paginationMode="client"
                rowCount={data?.length}
                getRowId={(row) => row.actionDate}
            />
        </MUIModal>
    )
}

export default withLayout(NPRListing);
